import { useFormikContext } from "formik";
import { FieldWrapper } from "../Additional/FieldWrapper";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { Label } from "@radix-ui/react-label"
import toast from "react-hot-toast";
import { AppProps } from "../AppProps";
import { Button } from "./button";

export interface FileProps extends AppProps {
  disabled?: boolean;
  title?: string;
  name: string;
  accept?: string;
  fileValue: File | null;
  required: boolean;
}

const RESUME_SIZE_LIMIT = 10;

enum FileState {
  Empty = 1,
  NotEmpty = 2,
  Error = 3
}

export function FileField({ disabled, title, name, accept, fileValue, required }: FileProps): JSX.Element {
  const { setFieldValue } = useFormikContext();
  const [isEmpty, setIsEmpty] = useState<FileState>(FileState.Empty);

  const inputClasses = classNames("border-gray-300 block w-full overflow-hidden cursor-pointer border",
    "text-gray-800 bg-white rounded-md mt-1 shadow-sm focus:outline-none focus:ring-2 focus:border-transparent",
    "dark:bg-slate-500 dark:text-gray-200 dark:border-black disabled:bg-slate-200 disabled:cursor-not-allowed");

  useEffect(() => {
    setIsEmpty(fileValue ? FileState.NotEmpty : FileState.Empty);
  }, [fileValue]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.currentTarget.files) {
      setIsEmpty(FileState.Empty);
      return;
    }

    if (e.currentTarget.files[0].size > (1024 * 1024 * RESUME_SIZE_LIMIT)) {
      toast.error(`Resume is too big. Please choose a resume less than ${RESUME_SIZE_LIMIT}MB.`)
      setIsEmpty(FileState.Error);
      return;
    }

    setIsEmpty(FileState.NotEmpty);
    setFieldValue(name, e.currentTarget.files[0]);
    toast.success("Resume succesfully uploaded!")
  };

  return (
    <>
      <FieldWrapper name={""}>
        <Label className="block text-md font-medium mb-3">
          {title}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
        <input className={inputClasses} type="file" id="fileInput" style={{ display: 'none' }} name={name} disabled={disabled} onChange={onChange} accept={accept} />
        {/* Notice: Do not wrap label around Button instead; it wouldn't work */}
        <Button
          type="button"
        >
          <label htmlFor="fileInput" className="cursor-pointer">
            Click here to upload your resume
          </label>
        </Button>
      </FieldWrapper>
      {isEmpty == FileState.Empty && <p className="font-bold text-red-600">No file was uploaded.</p>}
      {isEmpty == FileState.NotEmpty && <p className="font-bold text-green-600">Your resume is uploaded.</p>}
      {isEmpty == FileState.Error && <p className="font-bold text-yellow-600">There was error uploading your resume. File needs to be smaller than {RESUME_SIZE_LIMIT} megabytes.</p>}
    </>
  );
}
